import { ExportButton } from '@/common/buttons/ExportButton';
import { Grid } from '@/common/grid/Grid';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { far } from '@awesome.me/kit-b4fd369ea4/icons';
import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTab } from '../entityTabs/TabContext';
import { PivotColsButton } from './support/PivotColsButton';

const VIEWER_PLUGIN_CODE = 'PERSPECTIVE_TRX_PIVOT_VIEWER_PLUGIN';

export const PerspectiveTrxPivotViewer = () => {
  const rsrc = useRsrc();
  const module = useModuleFromRoute();

  const gridRef = useRef();

  // The pivot config: groupByCols are the grouping columns, amountCols are the summed columns (fieldDefs)
  const [groupByCols, setGroupByCols] = useState();
  const [amountCols, setAmountCols] = useState();

  // All columns of the underlying transactions, offered by the config dialog
  const [allCols, setAllCols] = useState();

  // The pivoted grid's fieldDefs, derived by the backend from the pivot query itself
  const [fieldDefs, setFieldDefs] = useState();

  const [exportReq, setExportReq] = useState();

  // NavState
  const { navState, assign } = useNavState();
  const { tabId } = useTab();
  const pivotTitle = navState.title?.startsWith('Pivot ') ? navState.title : 'Pivot ' + navState.title;
  const pivotNavStateId = tabId ? tabId + 'PivotTableState' : 'pivotTableState';
  const pivotNavState = navState[pivotNavStateId] ?? {};

  const buildReqParam = (operation) => ({
    entity: navState.entity,
    trxGroup: navState.trxGroup,
    fetchReqParam: navState.fetchReqParam,
    viewerPluginCode: VIEWER_PLUGIN_CODE,
    operation,
    groupByCols: groupByCols.map((fieldDef) => fieldDef.field),
    amountCols: amountCols.map((fieldDef) => fieldDef.field),
  });

  // The available columns come from the plain (unpivoted) viewer's fieldDefs; restore any saved config
  useEffect(() => {
    const reqParam = {
      entity: navState.entity,
      trxGroup: navState.trxGroup,
      fetchReqParam: navState.fetchReqParam,
      viewerPluginCode: 'PERSPECTIVE_TRX_VIEWER_PLUGIN',
      operation: 'FIELD_DEFS',
    };
    rsrc.data(module, 'VIEWER_DATA_PLUGIN', reqParam, navState.entity).then((fieldDefs) => {
      setAllCols(fieldDefs);
    });

    setGroupByCols(pivotNavState.groupByCols ?? []);
    setAmountCols(pivotNavState.amountCols ?? []);
  }, []);

  // A config change persists to navState and re-derives the pivoted grid's fieldDefs
  useEffect(() => {
    if (!groupByCols || !amountCols) return;

    assign(pivotNavStateId, { groupByCols, amountCols });

    if (groupByCols.length === 0 && amountCols.length === 0) {
      setFieldDefs(null);
      return;
    }

    rsrc.data(module, 'VIEWER_DATA_PLUGIN', buildReqParam('FIELD_DEFS'), navState.entity).then((fieldDefs) => {
      setFieldDefs(fieldDefs);
    });
  }, [groupByCols, amountCols]);

  const buildAndSetExportReq = (reqParam) => {
    const exportReq = {
      timeZoneName: DateUtils.timeZoneName(),
      displayName: navState.title + ' Pivot',
      module,
      entity: navState.entity,
      uiType: 'VIEWER',
      viewerPluginCode: VIEWER_PLUGIN_CODE,
      viewerReqParam: reqParam,
      fieldDefs,
    };
    setExportReq(exportReq);
  };

  const onGridReady = () => {
    const dataSource = {
      getRows: (params) => {
        const expr = ExprUtils.filterModelToExpr(params.filterModel, fieldDefs);

        const reqParam = {
          ...buildReqParam('ROWS'),
          expr,
          rowsReq: {
            startRow: params.startRow,
            endRow: params.endRow,
            expr,
            sortModel: params.sortModel,
          },
        };

        buildAndSetExportReq(reqParam);

        rsrc.data(module, 'VIEWER_DATA_PLUGIN', reqParam, navState.entity).then((res) => {
          if (gridRef.current) {
            gridRef.current.onGridSuccess(res, params);
            gridRef.current.resize();
          }
        });

        // Count and totals depend only on the filter, not the scrolled page, so fetch them once
        // per filter (first block) instead of on every infinite-scroll block.
        if (params.startRow === 0) {
          const summaryReqParam = {
            ...buildReqParam('AGGREGATES'),
            rowsReq: { expr },
          };
          rsrc.data(module, 'VIEWER_DATA_PLUGIN', summaryReqParam, navState.entity).then((summary) => {
            if (gridRef.current) {
              gridRef.current.setRowCount(summary.count);
              gridRef.current.setTotalsRow(summary.totals);
            }
          });
        }
      },
    };

    gridRef.current.setDataSource(dataSource);
  };

  const setPivotCols = ({ groupByCols, amountCols }) => {
    setGroupByCols(groupByCols);
    setAmountCols(amountCols);
  };

  // Columns not already grouped or summed remain available to the config dialog
  const availableCols = (allCols ?? []).filter(
    (fieldDef) =>
      !(groupByCols ?? []).some((groupByCol) => groupByCol.field === fieldDef.field) &&
      !(amountCols ?? []).some((amountCol) => amountCol.field === fieldDef.field),
  );

  return (
    allCols && (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Title icon={far.faTableColumns} title={pivotTitle} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PivotColsButton
              availableCols={availableCols}
              groupByCols={groupByCols}
              amountCols={amountCols}
              setPivotCols={setPivotCols}
            />
            <ExportButton exportReq={exportReq} />
          </Box>
        </Box>
        {/* Always render a grid; before a pivot is configured it simply has no columns/rows. */}
        <Grid
          key={JSON.stringify(fieldDefs)}
          ref={gridRef}
          gridOptions={{ fieldDefs: fieldDefs ?? [] }}
          events={{ onGridReady }}
        />
      </>
    )
  );
};
